import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ErpTeacherAiProfileQuery,
  Maybe,
  useApplyCourseSuggestionsMutation,
  useErpTeacherAiProfileQuery,
  useRecomputeAiProfileMutation,
  useRunInterviewAnalysisMutation,
} from 'core/graphql';

export type ITeacherAiProfile = NonNullable<
  ErpTeacherAiProfileQuery['teacherAiProfile']
>;
export type IAiSkill = ITeacherAiProfile['skills'][number];
export type IAiSuggestion = ITeacherAiProfile['suggestions'][number];
export type IAiRun = ITeacherAiProfile['runs'][number];
export type IAiSourceText = ITeacherAiProfile['summary'][number];

/** Lowercase strings on the wire (`teachers.interview_constants`). */
// Prefixed: `core/hooks` already re-exports an `AI_STATUSES` from the homework
// reviewer (spec 0012), and a duplicate name there is a hard tsc error.
export const TEACHER_AI_STATUSES = [
  'pending',
  'analyzing',
  'done',
  'failed',
] as const;
export type TeacherAiStatus = typeof TEACHER_AI_STATUSES[number];

export const TEACHER_AI_STATUS_COLOR: Record<TeacherAiStatus, string> = {
  pending: 'default',
  analyzing: 'blue',
  done: 'green',
  failed: 'red',
};

/** Skill levels, weakest first — the order the UI sorts and colours by. */
export const TEACHER_AI_LEVELS = [
  '',
  'none',
  'basic',
  'intermediate',
  'advanced',
  'expert',
] as const;

export const aiLevelRank = (level?: string | null): number =>
  Math.max(0, (TEACHER_AI_LEVELS as readonly string[]).indexOf(level || ''));

const TEACHER_AI_ERROR_CODES = [
  'AI_DISABLED',
  'AI_ERROR',
  'NO_TRANSCRIPT',
  'LOCK_TIMEOUT',
  'INVALID_INPUT',
  'NOT_FOUND',
] as const;

interface IAiCustomError {
  code?: Maybe<string>;
  message?: Maybe<string>;
}

export const aiErrorMessage = (
  t: (key: string) => string,
  customErrors?: Maybe<Array<Maybe<IAiCustomError>>>,
): string => {
  const first = (customErrors || []).find(Boolean);
  const code = first?.code || '';
  if ((TEACHER_AI_ERROR_CODES as readonly string[]).includes(code)) {
    return t(`pages.teacherDetail.ai.errors.${code}`);
  }
  return first?.message || t('pages.teacherDetail.ai.errors.generic');
};

interface IActionResult {
  success: boolean;
  errorMessage?: string;
}

/**
 * Spec 0028 C — the AI competency profile.
 *
 * `AI_DISABLED` and `NO_TRANSCRIPT` come back as ordinary customErrors, not
 * exceptions: both are PRECONDITIONS, and the panel is expected to stay visible
 * and explain itself rather than disappear.
 */
export const useTeacherAiProfile = (teacherId?: string, enabled = true) => {
  const { t } = useTranslation();
  const { data, loading, refetch } = useErpTeacherAiProfileQuery({
    // `|| ''` rather than a cast — see useTeacherInterviews; this query is
    // refetched by name from several mutations.
    variables: { teacherId: teacherId || '' },
    skip: !enabled || !teacherId,
    fetchPolicy: 'cache-and-network',
  });

  const refetchProfile = { refetchQueries: ['ErpTeacherAiProfile'] };
  const [analyzeRun] = useRunInterviewAnalysisMutation(refetchProfile);
  const [recomputeRun] = useRecomputeAiProfileMutation(refetchProfile);
  const [applyRun] = useApplyCourseSuggestionsMutation({
    // Accepting writes `coursesCanTeach`, which the Profile tab renders — so the
    // teacher card itself has to be refetched, not just this panel.
    // Имя ОПЕРАЦИИ (`query TeacherDetail`), а не типа, который сгенерил
    // codegen. `TeacherDetailQuery` не совпадал ни с чем, и карточка
    // преподавателя после принятия рекомендаций не обновлялась (ТЗ 0033).
    refetchQueries: ['ErpTeacherAiProfile', 'TeacherDetail'],
  });
  const [pending, setPending] = useState(false);

  const run = useCallback(
    async (
      call: () => Promise<
        | {
            success?: boolean | null;
            customErrors?: Maybe<Array<Maybe<IAiCustomError>>>;
          }
        | null
        | undefined
      >,
    ): Promise<IActionResult> => {
      setPending(true);
      try {
        const payload = await call();
        if (payload?.success) return { success: true };
        return {
          success: false,
          errorMessage: aiErrorMessage(t, payload?.customErrors),
        };
      } catch (e) {
        return { success: false, errorMessage: (e as Error).message };
      } finally {
        setPending(false);
      }
    },
    [t],
  );

  const analyzeInterview = useCallback(
    (interviewId: string) =>
      run(async () => {
        const result = await analyzeRun({ variables: { interviewId } });
        return result.data?.runTeacherInterviewAnalysis;
      }),
    [run, analyzeRun],
  );

  const recompute = useCallback(
    () =>
      run(async () => {
        const result = await recomputeRun({
          variables: { teacherId: teacherId as string },
        });
        return result.data?.recomputeTeacherAiProfile;
      }),
    [run, recomputeRun, teacherId],
  );

  const applySuggestions = useCallback(
    (suggestionIds: string[], action: 'accept' | 'reject') =>
      run(async () => {
        const result = await applyRun({
          variables: { teacherId: teacherId as string, suggestionIds, action },
        });
        return result.data?.applyTeacherCourseSuggestions;
      }),
    [run, applyRun, teacherId],
  );

  return {
    profile: data?.teacherAiProfile || undefined,
    loading,
    pending,
    refetch,
    analyzeInterview,
    recompute,
    applySuggestions,
  };
};
